/**
 * Authentication context provider for Firebase Auth.
 *
 * Manages authentication state and provides auth methods to components.
 * Pattern follows existing I18nContext.tsx structure.
 *
 * NOTE: The useAuth hook is exported from hooks/useAuth.ts for React Fast Refresh
 * compatibility. Fast Refresh works best when files only export components.
 *
 * In desktop (Electron) mode, Google Sign-In uses a loopback OAuth server
 * instead of popup/redirect for better compatibility.
 */

import * as Sentry from '@sentry/react';
import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';

import type { IAuthContext, IAuthUser, AuthProviderType } from '../types/auth.types';
import { firebaseClient, isFirebaseEnabled } from '../services/firebase';
import { web3AuthClient } from '../services/web3auth';
import type { IWeb3AuthUser } from '../services/web3auth';
import { isElectronMode as isDesktopMode } from '../utils/apiMode';
import { AUTH_ERRORS, ELECTRON_ERRORS, WEB3AUTH_ERRORS } from '../constants/errorMessages';
import { CURRENT_TOS_VERSION, TOS_FIELDS } from '../constants/termsOfService';

import { publishCredentialToLocalBackend } from '../services/credentialSession';
import { handleError } from '../utils/errorHandler';
import { WALLET_LOGIN_ENABLED } from '../constants/features';

/**
 * Authentication context.
 * Exported for use by useAuth hook in hooks/useAuth.ts
 *
 * Note: We disable react-refresh/only-export-components here because
 * exporting the Context is required for the separated useAuth hook pattern.
 * This is a valid architectural choice for React Fast Refresh compliance.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<IAuthContext | undefined>(undefined);

/**
 * Props for AuthProvider component.
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Get provider type from Firebase user.
 *
 * @param user - Firebase user
 * @returns Authentication provider type
 */
function getProviderType(user: User): AuthProviderType {
  const providerId = user.providerData[0]?.providerId;
  if (providerId === 'google.com') return 'google.com';
  if (providerId === 'facebook.com') return 'facebook.com';
  if (providerId === 'password') return 'password';
  return 'anonymous';
}

/**
 * Convert Firebase user to IAuthUser.
 *
 * @param user - Firebase user
 * @returns Simplified auth user object
 */
function convertToAuthUser(user: User): IAuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    provider: getProviderType(user),
  };
}

/**
 * Create or update user document in Firestore.
 * Returns ToS acceptance status to avoid a second Firestore read.
 * No-op if Firestore is not available (returns tosAccepted=true for local dev).
 *
 * @param user - Firebase user
 * @returns Object with tosAccepted boolean
 */
async function syncUserToFirestore(user: User): Promise<{ tosAccepted: boolean }> {
  const db = firebaseClient.getFirestore();
  if (!db) {
    console.warn('[AuthContext] Firestore not available - skipping user sync');
    return { tosAccepted: true };
  }

  const userRef = doc(db, 'users', user.uid);

  // Check if user document exists
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    // Update last login
    await updateDoc(userRef, {
      lastLogin: new Date(),
    });
    const data = userDoc.data();
    const acceptedVersion = data?.[TOS_FIELDS.ACCEPTED_VERSION];
    return { tosAccepted: acceptedVersion >= CURRENT_TOS_VERSION };
  } else {
    // Create new user document
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      provider: getProviderType(user),
      createdAt: new Date(),
      lastLogin: new Date(),
      metadata: {},
    });
    return { tosAccepted: false };
  }
}

/**
 * Convert Web3Auth user to IAuthUser.
 *
 * @param web3User - Web3Auth user info
 * @returns Simplified auth user object
 */
function convertWeb3AuthToAuthUser(web3User: IWeb3AuthUser): IAuthUser {
  return {
    uid: web3User.walletAddress, // Use wallet address as UID
    email: web3User.email ?? null,
    displayName: web3User.name ?? shortenWalletAddress(web3User.walletAddress),
    photoURL: web3User.profileImage ?? null,
    emailVerified: !!web3User.email,
    provider: 'web3auth',
    walletAddress: web3User.walletAddress,
    solBalance: web3User.balance,
  };
}

/**
 * Shorten wallet address for display.
 *
 * @param address - Full wallet address
 * @returns Shortened address (e.g., "ABC...XYZ")
 */
function shortenWalletAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

/**
 * Create or update Web3Auth user document in Firestore.
 * Returns ToS acceptance status to avoid a second Firestore read.
 * No-op if Firestore is not available (returns tosAccepted=true for local dev).
 *
 * @param web3User - Web3Auth user info
 * @returns Object with tosAccepted boolean
 */
async function syncWeb3UserToFirestore(web3User: IWeb3AuthUser): Promise<{ tosAccepted: boolean }> {
  const db = firebaseClient.getFirestore();
  if (!db) {
    console.warn('[AuthContext] Firestore not available - skipping Web3Auth user sync');
    return { tosAccepted: true };
  }

  const userRef = doc(db, 'users', web3User.walletAddress);

  // Check if user document exists
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    // Update last login
    await updateDoc(userRef, {
      lastLogin: new Date(),
      walletAddress: web3User.walletAddress,
    });
    const data = userDoc.data();
    const acceptedVersion = data?.[TOS_FIELDS.ACCEPTED_VERSION];
    return { tosAccepted: acceptedVersion >= CURRENT_TOS_VERSION };
  } else {
    // Create new user document
    await setDoc(userRef, {
      uid: web3User.walletAddress,
      email: web3User.email ?? null,
      displayName: web3User.name ?? shortenWalletAddress(web3User.walletAddress),
      photoURL: web3User.profileImage ?? null,
      provider: 'web3auth' as AuthProviderType,
      createdAt: new Date(),
      lastLogin: new Date(),
      metadata: {
        authMethod: web3User.authMethod,
      },
      walletAddress: web3User.walletAddress,
    });
    return { tosAccepted: false };
  }
}

/**
 * Authentication provider component.
 *
 * Wraps the application and provides authentication state and methods.
 *
 * @param props - Component props
 * @returns AuthProvider component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const isDesktop = isDesktopMode();
  const [user, setUser] = useState<IAuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [isWeb3AuthReady, setIsWeb3AuthReady] = useState<boolean>(false);
  const [tosAccepted, setTosAccepted] = useState<boolean>(false);
  const [tosLoading, setTosLoading] = useState<boolean>(true);

  /**
   * Initialize Web3Auth on mount.
   */
  useEffect(() => {
    if (!WALLET_LOGIN_ENABLED) return;

    const initWeb3Auth = async () => {
      try {
        await web3AuthClient.initialize();
        setIsWeb3AuthReady(true);

        // Check if already connected from previous session
        if (web3AuthClient.isConnected()) {
          const web3User = await web3AuthClient.getUserInfo();
          const authUser = convertWeb3AuthToAuthUser(web3User);
          setUser(authUser);
          Sentry.setUser({ id: authUser.uid });
          const { tosAccepted: accepted } = await syncWeb3UserToFirestore(web3User);
          setTosAccepted(accepted);
          setTosLoading(false);

          // Ensure Firebase auth is set up for protected features
          await web3AuthClient.ensureFirebaseAuth();
        }
      } catch (err) {
        console.error('[Web3Auth] Init error:', err);
        // Don't block app if Web3Auth fails
        setIsWeb3AuthReady(false);
      }
    };

    initWeb3Auth();
  }, []);

  /**
   * Initialize Firebase auth state listener on mount.
   */
  useEffect(() => {
    // Skip Firebase auth setup if not configured
    if (!isFirebaseEnabled) {
      console.warn('[AuthContext] Firebase disabled - auth state listener not initialized');
      setLoading(false);
      setTosAccepted(true);
      setTosLoading(false);
      return;
    }

    const auth = firebaseClient.getAuth();
    if (!auth) {
      console.warn('[AuthContext] Firebase Auth not available');
      setLoading(false);
      setTosAccepted(true);
      setTosLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // User is signed in via Firebase
          const authUser = convertToAuthUser(firebaseUser);
          setUser(authUser);
          Sentry.setUser({ id: authUser.uid });

          // Fetch custom claims (e.g., admin: true)
          try {
            const tokenResult = await firebaseUser.getIdTokenResult();
            setClaims(tokenResult.claims as Record<string, unknown>);
          } catch (claimsErr) {
            console.warn('[AuthContext] Failed to fetch claims:', claimsErr);
            setClaims(null);
          }

          // Sync user to Firestore and check ToS acceptance
          setTosLoading(true);
          syncUserToFirestore(firebaseUser)
            .then(({ tosAccepted: accepted }) => {
              setTosAccepted(accepted);
            })
            .catch((firestoreErr) => {
              console.warn('[AuthContext] Firestore sync failed (non-blocking):', firestoreErr);
              // Fail closed: show ToS modal if we can't verify acceptance
              setTosAccepted(false);
            })
            .finally(() => {
              setTosLoading(false);
            });
        } else if (!WALLET_LOGIN_ENABLED || !web3AuthClient.isConnected()) {
          // User is signed out (and not connected via Web3Auth)
          setUser(null);
          Sentry.setUser(null);
          setClaims(null);
          setTosAccepted(false);
          setTosLoading(false);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(handleError(err, 'AuthContext', 'Authentication error'));
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  /**
   * Keep the desktop backend's credential current.
   *
   * The backend forwards this token to Cloud Run for every billed operation, and
   * desktop jobs routinely outlive the hour an ID token lives. Ordinary requests
   * already refresh it, but a long job can run under a completely idle UI — so
   * push on every rotation too. onIdTokenChanged fires on sign-in, sign-out and
   * every refresh, which covers the initial publish as well.
   *
   * Kept separate from the onAuthStateChanged effect above so an hourly token
   * rotation does not re-run the Firestore/ToS sync.
   */
  useEffect(() => {
    if (!isDesktop || !isFirebaseEnabled) {
      return;
    }

    const auth = firebaseClient.getAuth();
    if (!auth) {
      return;
    }

    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        return;
      }
      void publishCredentialToLocalBackend(await firebaseUser.getIdToken());
    });

    return () => unsubscribe();
  }, [isDesktop]);

  /**
   * Sign in with Google.
   * Uses different flow for Electron (loopback OAuth) vs web (popup).
   */
  const signInWithGoogle = async (): Promise<void> => {
    if (!isFirebaseEnabled) {
      setError(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
      throw new Error(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
    }
    try {
      setLoading(true);
      setError(null);

      if (isDesktop) {
        // Electron desktop mode: use loopback OAuth flow
        const electronAPI = (
          window as unknown as {
            electronAPI?: {
              startGoogleOAuth?: (clientId: string) => Promise<{
                success: boolean;
                idToken?: string;
                accessToken?: string;
                error?: string;
              }>;
            };
          }
        ).electronAPI;

        if (!electronAPI?.startGoogleOAuth) {
          throw new Error(ELECTRON_ERRORS.OAUTH_API_NOT_AVAILABLE);
        }

        // Get Google OAuth Client ID from environment
        const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
        if (!clientId) {
          console.error('[AuthContext] VITE_GOOGLE_OAUTH_CLIENT_ID is not set in environment');
          throw new Error(ELECTRON_ERRORS.OAUTH_CLIENT_ID_NOT_CONFIGURED);
        }

        console.log('[AuthContext] Starting Electron OAuth flow...');
        const result = await electronAPI.startGoogleOAuth(clientId);

        if (!result.success) {
          throw new Error(result.error || 'OAuth failed');
        }

        if (!result.idToken) {
          throw new Error(ELECTRON_ERRORS.NO_ID_TOKEN);
        }

        console.log('[AuthContext] Got ID token, signing in with Firebase...');
        console.log('[AuthContext] ID token length:', result.idToken?.length);
        console.log('[AuthContext] Access token:', result.accessToken ? 'present' : 'missing');
        try {
          const userCredential = await firebaseClient.signInWithGoogleIdToken(
            result.idToken,
            result.accessToken
          );
          console.log(
            '[AuthContext] Firebase sign-in successful, user:',
            userCredential?.user?.email
          );
        } catch (firebaseErr) {
          console.error('[AuthContext] Firebase sign-in error:', firebaseErr);
          throw firebaseErr;
        }
      } else {
        // Web mode: use popup
        await firebaseClient.signInWithGoogle();
      }
      // User state will be updated by onAuthStateChanged
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Google sign-in failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with Facebook.
   */
  const signInWithFacebook = async (): Promise<void> => {
    if (!isFirebaseEnabled) {
      setError(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
      throw new Error(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
    }
    try {
      setLoading(true);
      setError(null);
      await firebaseClient.signInWithFacebook();
      // User state will be updated by onAuthStateChanged
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Facebook sign-in failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with email and password.
   *
   * @param email - User email
   * @param password - User password
   */
  const signInWithEmail = async (email: string, password: string): Promise<void> => {
    if (!isFirebaseEnabled) {
      setError(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
      throw new Error(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
    }
    try {
      setLoading(true);
      setError(null);
      await firebaseClient.signInWithEmail(email, password);
      // User state will be updated by onAuthStateChanged
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Email sign-in failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign up with email and password.
   *
   * @param email - User email
   * @param password - User password
   * @param displayName - Optional display name
   */
  const signUpWithEmail = async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<void> => {
    if (!isFirebaseEnabled) {
      setError(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
      throw new Error(AUTH_ERRORS.FIREBASE_NOT_CONFIGURED);
    }
    try {
      setLoading(true);
      setError(null);
      await firebaseClient.signUpWithEmail(email, password, displayName);
      // User state will be updated by onAuthStateChanged
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Email sign-up failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out current user (Firebase or Web3Auth).
   */
  const signOut = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Disconnect from Web3Auth if connected
      if (WALLET_LOGIN_ENABLED && web3AuthClient.isConnected()) {
        await web3AuthClient.disconnect();
      }

      // Sign out from Firebase
      await firebaseClient.signOut();
      setUser(null);
      Sentry.setUser(null);
      setClaims(null);
      setTosAccepted(false);
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Sign-out failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with Web3Auth (wallet or social).
   * Opens Web3Auth modal with all available options.
   */
  const signInWithWallet = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      if (!isWeb3AuthReady) {
        throw new Error(WEB3AUTH_ERRORS.NOT_READY);
      }

      const web3User = await web3AuthClient.connect();
      const authUser = convertWeb3AuthToAuthUser(web3User);
      setUser(authUser);
      Sentry.setUser({ id: authUser.uid });

      // Sync to Firestore and check ToS
      const { tosAccepted: accepted } = await syncWeb3UserToFirestore(web3User);
      setTosAccepted(accepted);
      setTosLoading(false);
    } catch (err) {
      const errorMessage = handleError(err, 'AuthContext', 'Wallet sign-in failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get Firebase ID token for authenticated user.
   *
   * @returns Promise with ID token or null
   */
  const getIdToken = async (): Promise<string | null> => {
    try {
      return await firebaseClient.getIdToken();
    } catch (err) {
      console.error('Error getting ID token:', err);
      return null;
    }
  };

  /**
   * Accept the current Terms of Service.
   * Writes acceptance timestamp and version to Firestore user document.
   */
  const acceptTermsOfService = async (): Promise<void> => {
    if (!user) return;

    const db = firebaseClient.getFirestore();
    if (!db) {
      // Local dev: just accept in memory
      setTosAccepted(true);
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      [TOS_FIELDS.ACCEPTED_AT]: new Date(),
      [TOS_FIELDS.ACCEPTED_VERSION]: CURRENT_TOS_VERSION,
    });
    setTosAccepted(true);
  };

  const value: IAuthContext = {
    user,
    loading,
    error,
    claims,
    signInWithGoogle,
    signInWithFacebook,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    getIdToken,
    signInWithWallet,
    isWeb3AuthReady,
    tosAccepted,
    tosLoading,
    acceptTermsOfService,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
